/**
 * First-run setup.
 *
 * Creates the restaurant and makes the signed-in account its owner. Everything
 * here can be changed later in Settings, so the form asks only for what is
 * needed to take an order: a name, a web address for the QR code, and the
 * timezone that every opening hour and daily total is measured against.
 */

import { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import {
  COUNTRIES,
  CURRENCIES,
  DEFAULT_CURRENCY,
  countryName,
  currencyForCountry,
  contactForCountry,
  detectCountry,
  fieldErrors,
  SLUG_TAKEN_MESSAGE,
  restaurantSetupSchema,
  slugify,
  zoneLabel,
  zonesForCountry,
} from '@yummy/shared';
import { createRestaurant } from '@yummy/db';
import {
  Button,
  Callout,
  Card,
  ErrorBanner,
  PageHeading,
  Screen,
  SelectField,
  Section,
  Stack,
  Text,
  TextField,
  useAction,
  useLocale,
  useTheme,
} from '@yummy/ui-rnr';

import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';

/**
 * Renders the first-run restaurant setup form.
 *
 * @returns The onboarding screen.
 */
export function OnboardingScreen() {
  const theme = useTheme();
  const { t, locale } = useLocale();
  const { refreshRestaurants } = useMerchant();
  const action = useAction();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  // The device's own zone, read once. It seeds the pickers rather than being
  // stored: what the restaurant keeps is a zone, chosen by country.
  const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const deviceCountry = detectCountry(
    typeof navigator === 'undefined' ? undefined : navigator.language,
    deviceZone,
  );

  // Country first, because it is what a merchant knows and it settles the
  // currency. The zone is only asked about where a country spans several, which
  // is Indonesia and Australia here.
  const [country, setCountry] = useState(() => deviceCountry ?? 'SG');
  const [zone, setZone] = useState(() => {
    const zones = zonesForCountry(deviceCountry ?? 'SG');
    return zones.includes(deviceZone) ? deviceZone : (zones[0] ?? deviceZone);
  });

  const zones = useMemo(() => zonesForCountry(country), [country]);
  const timezone = zones.includes(zone) ? zone : (zones[0] ?? zone);
  // Everything the device can tell us about where the merchant is: the currency,
  // and the shape of a local phone number and address. Re-read when the timezone
  // changes, so correcting the zone also corrects the examples.
  // Keyed on the country the merchant picked, so choosing a different country
  // changes the examples with it. Inferring from the device would answer from
  // the locale and ignore the picker.
  const contact = useMemo(() => contactForCountry(country), [country]);

  const [currency, setCurrency] = useState(
    () => currencyForCountry(country) ?? DEFAULT_CURRENCY,
  );

  // Named in the app's language, not the device's: a German device reading an
  // English screen must not be offered "Singapur".
  const countryOptions = useMemo(
    () => COUNTRIES.map((code) => ({ value: code, label: countryName(code, locale) })),
    [locale],
  );
  const zoneOptions = useMemo(
    () => zones.map((value) => ({ value, label: zoneLabel(value) })),
    [zones],
  );

  const chooseCountry = useCallback(
    (next: string) => {
      setCountry(next);
      const nextZones = zonesForCountry(next);
      setZone(nextZones.includes(zone) ? zone : (nextZones[0] ?? zone));
      // The country settles the money; the merchant can still pick another.
      const nextCurrency = currencyForCountry(next);
      if (nextCurrency) setCurrency(nextCurrency);
    },
    [zone],
  );
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The web address follows the name until the merchant edits it themselves.
  const effectiveSlug = slugTouched ? slug : slugify(name);


  const submit = useCallback(async () => {
    const parsed = restaurantSetupSchema.safeParse({
      name,
      slug: effectiveSlug,
      timezone,
      currency,
      country,
      phone,
      address,
      description,
    });

    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      return;
    }
    setErrors({});

    const created = await action.run(async () => {
      try {
        return await createRestaurant(supabase, parsed.data);
      } catch (cause) {
        // The only thing that knows whether an address is free is the database,
        // so this arrives from the server. Handled here rather than rethrown, so
        // it lands under the address field instead of in the banner above.
        const message = cause instanceof Error ? cause.message : String(cause);
        if (message.includes(SLUG_TAKEN_MESSAGE)) {
          setErrors({ slug: SLUG_TAKEN_MESSAGE });
          return null;
        }
        throw cause;
      }
    });
    if (created) await refreshRestaurants();
  }, [name, effectiveSlug, timezone, currency, phone, address, description, action, refreshRestaurants]);

  const createLabel = t('merchant.onboarding.create');

  return (
    <Screen columnClassName="max-w-lg">
      <Stack gap="xl">
        <PageHeading
          title={t('merchant.onboarding.title')}
          subtitle={t('merchant.onboarding.intro')}
        />

        <Card className="p-4">
          <Stack gap="lg">
            <Section title={t('merchant.onboarding.basics')}>
              <Stack gap="lg">
                <TextField
                  label={t('merchant.onboarding.restaurantName')}
                  required
                  value={name}
                  onChangeText={setName}
                  error={errors.name}
                  placeholder={t('merchant.onboarding.restaurantNamePlaceholder')}
                  autoComplete="organization"
                />
                <TextField
                  label={t('merchant.onboarding.webAddress')}
                  required
                  value={effectiveSlug}
                  onChangeText={(value) => {
                    setSlugTouched(true);
                    setSlug(value);
                  }}
                  error={errors.slug}
                  autoCapitalize="none"
                  autoCorrect={false}
                  hint={t('merchant.onboarding.webAddressHint')}
                  placeholder={t('merchant.onboarding.webAddressPlaceholder')}
                />
                <TextField
                  label={t('common.description')}
                  value={description}
                  onChangeText={setDescription}
                  error={errors.description}
                  multiline
                  numberOfLines={3}
                  placeholder={t('merchant.onboarding.descriptionPlaceholder')}
                />
              </Stack>
            </Section>

            <Section
              title={t('merchant.onboarding.locationAndMoney')}
              subtitle={t('merchant.onboarding.locationAndMoneyHint')}>
              <Stack gap="lg">
                <Stack gap="xs">
                  <Text className="text-muted-foreground text-sm font-semibold">
                    {t('common.country')}
                  </Text>
                  <SelectField
                    options={countryOptions}
                    value={country}
                    onChange={chooseCountry}
                    label={t('common.country')}
                  />
                </Stack>

                {/* Only where a country spans more than one zone. Everywhere
                    else the country alone decides the local time. */}
                {zones.length > 1 ? (
                  <Stack gap="xs">
                    <Text className="text-muted-foreground text-sm font-semibold">
                      {t('common.timezone')}
                    </Text>
                    <SelectField
                      options={zoneOptions}
                      value={timezone}
                      onChange={setZone}
                      label={t('common.timezone')}
                    />
                  </Stack>
                ) : null}

                <Stack gap="xs">
                  <Text className="text-muted-foreground text-sm font-semibold">
                    {t('common.currency')}
                  </Text>
                  <SelectField
                    options={CURRENCIES.map((code) => ({ value: code, label: code }))}
                    value={currency}
                    onChange={setCurrency}
                    label={t('common.currency')}
                  />
                </Stack>

                <TextField
                  label={t('common.phone')}
                  value={phone}
                  onChangeText={setPhone}
                  error={errors.phone}
                  keyboardType="phone-pad"
                  placeholder={contact.phonePlaceholder}
                />
                <TextField
                  label={t('common.address')}
                  required
                  value={address}
                  onChangeText={setAddress}
                  error={errors.address}
                  placeholder={contact.addressPlaceholder}
                  hint={t('merchant.onboarding.addressHint')}
                />
              </Stack>
            </Section>

            {action.error ? <ErrorBanner message={action.error} /> : null}

            <Callout
              tone="info"
              title={t('merchant.onboarding.pickupOnly')}
              message={t('merchant.onboarding.pickupOnlyHint')}
            />

            <Button
              onPress={submit}
              disabled={action.pending}
              size="lg"
              accessibilityLabel={createLabel}
              aria-busy={action.pending}
              className="w-full">
              {action.pending ? (
                <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
              ) : (
                <Text>{createLabel}</Text>
              )}
            </Button>
          </Stack>
        </Card>
      </Stack>
    </Screen>
  );
}

/**
 * A single-choice dropdown.
 *
 * The Reusables `Select` rather than a row of chips: seventeen timezones as
 * chips is a wall of buttons that pushes the rest of the form off the screen,
 * and it is slower to scan than a list. It is also not a DOM `<select>` — it is
 * the same cross-platform primitive the rest of the kit uses, styled with
 * `className` and rendered through the portal host.
 *
 * @param props Options, current value, handler, and an accessible label.
 * @returns The dropdown.
 */
